import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Button, Card, Col, Container, OverlayTrigger, Table, Tooltip, Form } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../../components/layout/pageTitle/PageTitle';
import useUserList from '../../hooks/api/users/useUserList';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import ModalComponent from '../../components/modal/ModalComponent';
import useInvite from '../../hooks/api/users/useInvite';
import useDeleteUser from '../../hooks/api/users/useDeleteUser';
import PaginationComponent from '../../components/pagination/PaginationComponent';
import { Formik } from 'formik';

const badgeNames = {
  PENDING: { badge: 'badge-warning', text: 'userList.status.pending' },
  ACTIVE: { badge: 'badge-success', text: 'userList.status.active' },
};

const UsersPage: FC = () => {
  const intl = useIntl();
  const { users, setOrganizationId, getUsersByPage, isLoading, currentPage, totalCount, pageSize } = useUserList();
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const { createInvitation } = useInvite();
  const { deleteUser } = useDeleteUser();

  const UserEmailSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({ id: 'fields.fieldErrors.email' }))
      .required(),
  });

  const renderAdministrators = (intl, users: any) => {
    return (
      <Table>
        <thead>
          <tr>
            <th className="text-start form-list-header">
              <FormattedMessage id={'userList.columns.name'}></FormattedMessage>
            </th>
            <th className="text-center form-list-header">
              <FormattedMessage id={'userList.columns.status'}></FormattedMessage>
            </th>
            <th className="text-center form-list-header">
              <FormattedMessage id={'userList.columns.actions'}></FormattedMessage>
            </th>
          </tr>
        </thead>
        <tbody>
          {users?.map((user, index) => (
            <tr className="submission-row" key={index}>
              <td className="text-start text-column d-flex align-items-center">
                {!!user.avatar && (
                  <span>
                    <img className="avatar rounded-circle" src={user.avatar}></img>
                  </span>
                )}
                {!user.avatar && <span className="avatar mdi mdi-account-circle" style={{ fontSize: '44px' }}></span>}
                <div className="ms-2" style={{ lineHeight: '20px' }}>
                  <div>
                    {user.firstName} {user.name}
                  </div>
                  <div>{user.email}</div>
                </div>
              </td>
              <td className="text-center">
                <span className={`badge ${badgeNames[user.status].badge}`}>
                  {intl.formatMessage({ id: badgeNames[user.status].text })}
                </span>
              </td>
              <td className="text-center">
                <OverlayTrigger
                  delay={{ show: 50, hide: 100 }}
                  overlay={<Tooltip id="button-tooltip">{'formList.buttons.delete'}</Tooltip>}
                  placement="top"
                >
                  <Button
                    className="d-inline-flex align-items-center justify-content-center"
                    variant="primary"
                    onClick={async () => {
                      await deleteUser(organizationId, user);
                      getUsersByPage(1);
                    }}
                  >
                    <span className="mdi mdi-trash-can-outline"></span>
                  </Button>
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const close = () => setOpenAddModal(false);

  const renderAddUserModal = () => {
    return (
      <Formik
        initialValues={{
          email: '',
        }}
        validationSchema={UserEmailSchema}
        onSubmit={async (values, { resetForm }) => {
          await createInvitation(organizationId, values.email);
          getUsersByPage(1);
          close();
          resetForm();
        }}
      >
        {({ errors, touched, values, handleChange, handleBlur, handleSubmit, resetForm }) => (
          <ModalComponent
            closeSettings={() => {
              resetForm();
              close();
            }}
            buttons={[
              {
                text: intl.formatMessage({ id: 'add' }),
                buttonVariant: 'primary',
                onClick: async () => {
                  handleSubmit();
                },
              },
              {
                text: intl.formatMessage({ id: 'cancel' }),
                buttonVariant: 'secondary',
                onClick: () => {
                  resetForm();
                  close();
                },
              },
            ]}
            show={openAddModal}
            modalHeading={intl.formatMessage({
              id: 'userList.addModal.addAdministrator',
            })}
          >
            <div className="d-flex justify-content-center flex-column px-5">
              <label className="modal-label">
                <Form.Label>{intl.formatMessage({ id: 'userList.addModal.email' })}</Form.Label>
                <Form.Control
                  disabled={isLoading}
                  type="email"
                  name="email"
                  placeholder={intl.formatMessage({ id: 'userList.addModal.email' })}
                  isInvalid={touched.email && !!errors.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  style={{ background: 'white' }}
                />
                <Form.Control.Feedback className="text-center" type="invalid">
                  *{errors.email && <span>{errors.email}</span>}
                </Form.Control.Feedback>
              </label>
            </div>
          </ModalComponent>
        )}
      </Formik>
    );
  };

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
    }
  }, [organizationId]);

  return (
    <React.Fragment>
      {renderAddUserModal()}
      <div className="page-content users-page">
        <Container fluid>
          <PageTitle title={intl.formatMessage({ id: 'navbar.settings.users.title' })}></PageTitle>
          <Col xl={12} className="text-end mb-2">
            <Button variant="primary" className="me-1" onClick={() => setOpenAddModal(true)}>
              <span className="mdi mdi-plus pe-1"></span>
              <span>{intl.formatMessage({ id: 'userList.buttons.add' })}</span>
            </Button>
          </Col>
          <Card>
            <Card.Body>{renderAdministrators(intl, users)}</Card.Body>
          </Card>
          {!isLoading && users && users.length > 0 && (
            <PaginationComponent
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageSize}
              onChange={(val) => getUsersByPage(val)}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UsersPage;
